import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Pill, SectionCard } from "@/components/ui-bits";
import { formatINR2, fmtDate, statusTone, today } from "@/lib/format";
import { BACKEND_URL } from "@/lib/config";
import { ArrowLeft, Download, Mail, Plus, Wallet, FileText } from "lucide-react";
import { toast } from "sonner";

const PMETHODS = ["Bank Transfer", "UPI", "Razorpay", "Cash", "Card", "PayPal", "Other"];

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [brand, setBrand] = useState(null);
  const [client, setClient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, method: "Bank Transfer", reference: "", date: today(), notes: "" });
  const [emailForm, setEmailForm] = useState({ to: "", subject: "", message: "" });

  const load = useCallback(async () => {
    const r = await api.get(`/invoices/${id}`);
    setInvoice(r.data);
    setPay((p) => ({ ...p, amount: r.data.balance_due }));
    const [b, c, ps] = await Promise.all([
      api.get("/brands").then((x) => x.data.find((b) => b.id === r.data.brand_id)),
      r.data.client_id ? api.get("/clients").then((x) => x.data.find((c) => c.id === r.data.client_id)) : null,
      api.get("/payments").then((x) => x.data.filter((p) => p.invoice_id === id)),
    ]);
    setBrand(b); setClient(c); setPayments(ps);
    setEmailForm((f) => ({ ...f, to: c?.email || "" }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!invoice) return <div className="text-muted-foreground text-sm">Loading…</div>;

  const recordPayment = async () => {
    try {
      await api.post("/payments", { ...pay, invoice_id: id, amount: Number(pay.amount) });
      toast.success("Payment recorded"); setPayOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendEmail = async () => {
    try {
      const r = await api.post(`/invoices/${id}/send`, emailForm);
      if (r.data.sent) toast.success("Invoice emailed");
      else toast.error(r.data.error || "Email send failed (configure Resend in Settings)");
      setSendOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pdfUrl = `${BACKEND_URL}/api/invoices/${id}/pdf`;
  const token = localStorage.getItem("ah_token");

  // Download via fetch (auth) and open as blob
  const downloadPdf = async () => {
    try {
      const r = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div data-testid="invoice-detail-page">
      <button onClick={() => nav(-1)} className="text-xs text-muted-foreground hover:text-bronze flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to invoices
      </button>

      <PageHeader
        eyebrow={brand?.name}
        title={<span className="font-mono">{invoice.invoice_number}</span>}
        description={(invoice.doc_type || "").replace("_", " ")}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadPdf} className="border-border bg-white" data-testid="invoice-download-pdf">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
            <Button variant="outline" onClick={() => setSendOpen(true)} className="border-border bg-white" data-testid="invoice-send-email">
              <Mail className="w-4 h-4 mr-1.5" /> Send
            </Button>
            <Button onClick={() => setPayOpen(true)} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="invoice-record-payment">
              <Plus className="w-4 h-4 mr-1.5" /> Record payment
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <SectionCard title="Status">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium border ${statusTone(invoice.status)}`}>{invoice.status}</span>
          <div className="mt-4 text-xs text-muted-foreground">
            Issued <span className="text-slate-900">{fmtDate(invoice.issue_date)}</span> · Due <span className="text-slate-900">{fmtDate(invoice.due_date)}</span>
          </div>
        </SectionCard>
        <SectionCard title="Bill to">
          <div className="text-sm font-medium">{client?.company || client?.name || "—"}</div>
          <div className="text-xs text-muted-foreground whitespace-pre-line">{client?.billing_address}</div>
          {client?.gstin && <div className="text-xs text-muted-foreground mt-1 font-mono">GSTIN {client.gstin}</div>}
        </SectionCard>
        <SectionCard title="Amounts">
          <div className="text-sm space-y-1 font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatINR2(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>{formatINR2(invoice.gst_total)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="text-muted-foreground">Total</span><span className="font-semibold">{formatINR2(invoice.total)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span>{formatINR2(invoice.paid_amount)}</span></div>
            <div className="flex justify-between text-bronze font-semibold"><span>Balance</span><span>{formatINR2(invoice.balance_due)}</span></div>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Items" className="mb-5">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Description</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Qty</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Rate</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">GST</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoice.items.map((it, i) => (
              <TableRow key={i} className="border-border hover:bg-[#f8f6f2]">
                <TableCell className="text-sm">{it.description}</TableCell>
                <TableCell className="text-right font-mono text-sm">{it.quantity}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatINR2(it.rate)}</TableCell>
                <TableCell className="text-right font-mono text-xs text-muted-foreground">{it.gst_rate}%</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatINR2(it.quantity * it.rate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <SectionCard title="Payments" action={
        <Button size="sm" variant="ghost" onClick={() => setPayOpen(true)} className="text-bronze">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      }>
        {payments.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">No payments recorded yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Method</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Reference</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} className="border-border hover:bg-[#f8f6f2]">
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(p.date)}</TableCell>
                  <TableCell className="text-sm">{p.method}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{p.reference || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-bronze">{formatINR2(p.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Amount</Label><Input type="number" value={pay.amount} onChange={(e)=>setPay({...pay, amount:e.target.value})} className="bg-white" data-testid="payment-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={pay.date} onChange={(e)=>setPay({...pay, date:e.target.value})} className="bg-white" /></div>
            <div className="col-span-2">
              <Label>Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{PMETHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Reference</Label><Input value={pay.reference} onChange={(e)=>setPay({...pay, reference:e.target.value})} className="bg-white font-mono" placeholder="UTR / Txn id" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={pay.notes} onChange={(e)=>setPay({...pay, notes:e.target.value})} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={recordPayment} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="save-payment-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Send invoice by email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>To</Label><Input value={emailForm.to} onChange={(e)=>setEmailForm({...emailForm, to:e.target.value})} className="bg-white" /></div>
            <div><Label>Subject (optional)</Label><Input value={emailForm.subject} onChange={(e)=>setEmailForm({...emailForm, subject:e.target.value})} className="bg-white" /></div>
            <div><Label>Message</Label><Textarea rows={3} value={emailForm.message} onChange={(e)=>setEmailForm({...emailForm, message:e.target.value})} className="bg-white" /></div>
            <div className="text-xs text-muted-foreground">Resend API key must be set in <Link to="/settings" className="text-bronze hover:underline">Settings</Link> to dispatch.</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} className="bg-bronze text-black hover:bg-[#d4af68]">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
